/**
 * Helpers de formato del módulo Activa — puros y sin dependencias de mockData.
 * (Antes vivían dentro de mockData.ts, lo que obligaba a importar datos falsos
 * para poder formatear datos reales.)
 */

export const money = (n: number) => "$ " + Number(n || 0).toLocaleString("es-AR");

export const compact = (n: number) => {
  const v = Number(n) || 0;
  // con coma decimal, como el resto de los montos: «$ 23.8M» al lado de «$ 27.681.246» se leía como 23 mil 800
  if (Math.abs(v) >= 1e6) return "$ " + (v / 1e6).toLocaleString("es-AR", { maximumFractionDigits: 1 }) + "M";
  if (Math.abs(v) >= 1e3) return "$ " + Math.round(v / 1e3) + "k";
  return money(v);
};

export const num = (n: number) => Number(n || 0).toLocaleString("es-AR");

/**
 * Concuerda una frase con la cantidad, sin repetir el número. Pluralizar por concatenación
 * ("1 mensajes", "1 personas volvieron") es el error que más rápido le baja la confianza al
 * dueño: si el texto está mal escrito, el número también parece mal calculado.
 *   plural(1, "mensaje quedó", "mensajes quedaron") → "mensaje quedó"
 * Se pasan las DOS formas completas (no un sufijo) porque en castellano cambian el sustantivo
 * Y el verbo a la vez, y a veces de forma irregular.
 */
export const plural = (n: number, singular: string, plural: string) =>
  Math.abs(Number(n) || 0) === 1 ? singular : plural;

/** % de `parte` sobre `total`. Devuelve 0 si no hay denominador (nunca NaN). */
export const pct = (parte: number, total: number) => (total ? Math.round((Number(parte) / Number(total)) * 100) : 0);

export const fecha = (d?: string | Date) => {
  if (!d) return "—";
  const x = new Date(d);
  return isNaN(x.getTime()) ? "—" : x.toLocaleDateString("es-AR", { day: "2-digit", month: "short", year: "numeric" });
};

export const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

/** «2026-10» → «octubre de 2026». El período de una campaña se mostraba crudo («Período 2026-10»). Lo que no entiende lo devuelve igual. */
export const mesLargo = (periodo?: string) => {
  const m = /^(\d{4})-(\d{2})$/.exec(String(periodo || ""));
  const mes = m ? MESES[Number(m[2]) - 1] : undefined;
  return m && mes ? `${mes} de ${m[1]}` : String(periodo || "—");
};

/**
 * «2026-07-11» (un día sin hora, como la fecha de corte del análisis) → «11 jul 2026». Con `new Date("2026-07-11")`
 * es medianoche UTC y en Argentina se mostraba el día anterior.
 */
export const fechaDia = (iso?: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
  return m ? fecha(new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))) : "—";
};

export const fechaHora = (d?: string | Date) => {
  if (!d) return "—";
  const x = new Date(d);
  return isNaN(x.getTime()) ? "—" : x.toLocaleString("es-AR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
};

/** Enmascara PII para mostrar en pantalla: +54 9 11 ****-6266 · j***@gmail.com */
export const maskContacto = (v?: string) => {
  const s = String(v || "");
  if (!s) return "—";
  if (s.includes("@")) {
    const [u, dom] = s.split("@");
    return (u[0] || "") + "***@" + dom;
  }
  return s.length > 4 ? s.slice(0, -4).replace(/\d/g, "*") + s.slice(-4) : s;
};
